import React from "react";
import { ErrorBoundary } from "react-error-boundary";
import { graphql, useLazyLoadQuery } from "react-relay";
import { PageSuspense } from "src/components/ui/page-suspense";

import { ArtifactCardQuery, ArtifactCardQuery$data } from "./__generated__/ArtifactCardQuery.graphql";
import { ArtifactCard as ArtifactCardContent } from "./ArtifactCardContent";

const artifactQuery = graphql`
  query ArtifactCardQuery($artifactId: String!) {
    artifact(artifactId: $artifactId) {
      id
      artifactType
      value
    }
  }
`;

type ArtifactCardData = NonNullable<ArtifactCardQuery$data["artifact"]>;

type PrefetchedArtifact = Pick<ArtifactCardData, "artifactType" | "value">;

interface ArtifactCardProps {
  artifactId: string | number;
  groupTags: readonly string[];
  title: string;
  description: string;
  query: string;
  isHighlighted?: boolean;
  sourceLink?: string;
  sourceName?: string;
  onHighlightCard?: (id: string) => void;
  onRemoveHighlight?: (id: string) => void;
  prefetchedArtifact?: PrefetchedArtifact;
}

function deriveCount(value: ArtifactCardData["value"]): number {
  if (Array.isArray(value)) {
    return value.length || 1;
  }

  if (
    value &&
    typeof value === "object" &&
    "rows" in value &&
    Array.isArray((value as { rows?: unknown[] }).rows)
  ) {
    const rows = (value as { rows?: unknown[] }).rows ?? [];
    return rows.length || 1;
  }

  return 1;
}

function ArtifactCardWithData({ artifactId, prefetchedArtifact, ...props }: ArtifactCardProps) {
  if (prefetchedArtifact) {
    return (
      <ArtifactCardContent
        count={deriveCount(prefetchedArtifact.value)}
        artifactValue={prefetchedArtifact.value}
        artifactType={prefetchedArtifact.artifactType}
        {...props}
      />
    );
  }

  const data = useLazyLoadQuery<ArtifactCardQuery>(artifactQuery, {
    artifactId: artifactId.toString(),
  });

  if (!data.artifact) {
    return null;
  }

  return (
    <ArtifactCardContent
      count={deriveCount(data.artifact.value)}
      artifactValue={data.artifact.value}
      artifactType={data.artifact.artifactType}
      {...props}
    />
  );
}

export function ArtifactCard(props: ArtifactCardProps) {
  return (
    <ErrorBoundary fallback={null}>
      <PageSuspense>
        <ArtifactCardWithData {...props} />
      </PageSuspense>
    </ErrorBoundary>
  );
}
